import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (UTC-10)' },
  { value: 'America/Anchorage', label: 'Alaska (UTC-9)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8)' },
  { value: 'America/Denver', label: 'Mountain Time (UTC-7)' },
  { value: 'America/Chicago', label: 'Central Time (UTC-6)' },
  { value: 'America/New_York', label: 'Eastern Time (UTC-5)' },
  { value: 'Europe/London', label: 'London (UTC+0)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10)' },
];

export default function SettingsDialog({ open, onOpenChange }) {
  const { user, logout } = useAuth();
  const { fetchSettings: refreshGlobalSettings } = useSettings();
  const navigate = useNavigate();

  // Settings state
  const [autoFillLimit, setAutoFillLimit] = useState(7);
  const [archiveDeleteDays, setArchiveDeleteDays] = useState(60);
  const [primaryColor, setPrimaryColor] = useState('#416ec8');
  const [secondaryColor, setSecondaryColor] = useState('#e9f5fb');
  const [userTimezone, setUserTimezone] = useState('UTC');
  const [dragHandlePosition, setDragHandlePosition] = useState('right');
  const [contextsEnabled, setContextsEnabled] = useState(false);
  const [assigneeTagsEnabled, setAssigneeTagsEnabled] = useState(false);
  const [horizontalGroups, setHorizontalGroups] = useState(true);
  const [energyTagsEnabled, setEnergyTagsEnabled] = useState(true);
  const [storageActiveVisibility, setStorageActiveVisibility] = useState('visible');
  const [taskCardTagsDisplay, setTaskCardTagsDisplay] = useState('icon_text');
  const [showTooltips, setShowTooltips] = useState(() => {
    const saved = localStorage.getItem('showTooltips');
    return saved === null ? true : saved === 'true';
  });

  // Password change state
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  // UI states
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [showResetColorsConfirm, setShowResetColorsConfirm] = useState(false);
  const [showRestoreTagsConfirm, setShowRestoreTagsConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState('account');

  const isGoogleUser = user?.email?.includes('@');

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  // Apply colors when they change
  useEffect(() => {
    document.documentElement.style.setProperty('--color-primary', primaryColor);
    document.documentElement.style.setProperty('--color-secondary', secondaryColor);
  }, [primaryColor, secondaryColor]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`, { withCredentials: true });
      const settings = response.data;
      setAutoFillLimit(settings.auto_fill_limit || 7);
      setArchiveDeleteDays(settings.archive_delete_days || 60);
      setPrimaryColor(settings.primary_color || '#416ec8');
      setSecondaryColor(settings.secondary_color || '#e9f5fb');
      setUserTimezone(settings.timezone || 'UTC');
      setDragHandlePosition(settings.drag_handle_position || 'right');
      setContextsEnabled(settings.contexts_enabled || false);
      setAssigneeTagsEnabled(settings.assignee_tags_enabled || false);
      setHorizontalGroups(settings.horizontal_groups || false);
      setEnergyTagsEnabled(settings.energy_tags_enabled !== false);
      setStorageActiveVisibility(settings.storage_active_visibility || 'visible');
      setTaskCardTagsDisplay(settings.task_card_tags_display || 'icon_text');
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings`, {
        auto_fill_limit: autoFillLimit,
        archive_delete_days: archiveDeleteDays,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        timezone: userTimezone,
        drag_handle_position: dragHandlePosition,
        contexts_enabled: contextsEnabled,
        assignee_tags_enabled: assigneeTagsEnabled,
        horizontal_groups: horizontalGroups,
        energy_tags_enabled: energyTagsEnabled,
        storage_active_visibility: storageActiveVisibility,
        task_card_tags_display: taskCardTagsDisplay,
      }, { withCredentials: true });
      
      toast.success('Settings saved');
      await refreshGlobalSettings();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword) {
      toast.error('Please fill in both password fields');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: oldPassword,
        new_password: newPassword,
      }, { withCredentials: true });
      
      toast.success('Password changed successfully');
      setShowPasswordChange(false);
      setOldPassword('');
      setNewPassword('');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out');
      onOpenChange(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      toast.error('Failed to logout');
    }
  };

  const handleResetColors = async () => {
    setPrimaryColor('#416ec8');
    setSecondaryColor('#e9f5fb');
    setShowResetColorsConfirm(false);
  };

  const handleRestoreTags = async () => {
    setRestoring(true);
    try {
      await axios.post(`${API}/tags/restore-defaults`, {}, { withCredentials: true });
      toast.success('Tags restored to defaults');
      setShowRestoreTagsConfirm(false);
    } catch (error) {
      console.error('Error restoring tags:', error);
      toast.error('Failed to restore tags');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading settings...</div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="settings-dialog max-w-md max-h-[85vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="h-font text-xl">Settings</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden bg-background">
          <TabsList className="grid w-full grid-cols-3 mb-4 gap-2 !rounded-lg">
            <TabsTrigger value="account" className="flex-1">Account</TabsTrigger>
            <TabsTrigger value="features" className="flex-1">Features</TabsTrigger>
            <TabsTrigger value="appearance" className="flex-1">Appearance</TabsTrigger>
          </TabsList>

          {/* Account Tab */}
          <TabsContent value="account" className="flex-1 overflow-y-auto space-y-4 pr-2 bg-background">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-sm">Username</Label>
              <p className="font-medium">{user?.username || 'N/A'}</p>
            </div>

            {isGoogleUser && (
              <div className="space-y-1">
                <Label className="text-muted-foreground text-sm">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              <Label htmlFor="timezone">Timezone</Label>
              <Select value={userTimezone} onValueChange={setUserTimezone} disabled={saving}>
                <SelectTrigger id="timezone" className="w-[180px]">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {!isGoogleUser && (
              <div className="space-y-4">
                {!showPasswordChange ? (
                  <Button
                    variant="outline"
                    onClick={() => setShowPasswordChange(true)}
                    className="w-full"
                  >
                    Change Password
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="old-password">Current Password</Label>
                      <Input
                        id="old-password"
                        type="password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        disabled={changingPassword}
                        className="text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new-password">New Password</Label>
                      <Input
                        id="new-password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={changingPassword}
                        className="text-white"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleChangePassword}
                        disabled={changingPassword}
                        style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
                        className="flex-1"
                      >
                        {changingPassword ? 'Changing...' : 'Update Password'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowPasswordChange(false);
                          setOldPassword('');
                          setNewPassword('');
                        }}
                        disabled={changingPassword}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <Button
              variant="destructive"
              onClick={handleLogout}
              className="w-full"
            >
              Log Out
            </Button>
          </TabsContent>

          {/* Features Tab */}
          <TabsContent value="features" className="flex-1 overflow-y-auto space-y-4 pr-2 bg-background">
            <div className="flex items-center justify-between">
              <Label htmlFor="contexts-enabled">Contexts</Label>
              <Select 
                value={contextsEnabled ? "enabled" : "disabled"} 
                onValueChange={(value) => setContextsEnabled(value === "enabled")} 
                disabled={saving}
              >
                <SelectTrigger id="contexts-enabled" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">Enabled</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="assignee-tags-enabled">Assignee Tags</Label>
              <Select 
                value={assigneeTagsEnabled ? "enabled" : "disabled"} 
                onValueChange={(value) => setAssigneeTagsEnabled(value === "enabled")} 
                disabled={saving}
              >
                <SelectTrigger id="assignee-tags-enabled" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">Enabled</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="energy-tags-enabled">Energy Tags</Label>
              <Select 
                value={energyTagsEnabled ? "enabled" : "disabled"} 
                onValueChange={(value) => setEnergyTagsEnabled(value === "enabled")} 
                disabled={saving}
              >
                <SelectTrigger id="energy-tags-enabled" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="enabled">Enabled</SelectItem>
                  <SelectItem value="disabled">Disabled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="auto-fill-limit">Auto Fill Task Limit</Label>
              <Input
                id="auto-fill-limit"
                type="number"
                min="1"
                max="50"
                value={autoFillLimit}
                onChange={(e) => setAutoFillLimit(parseInt(e.target.value) || 1)}
                disabled={saving}
                className="w-[120px]"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="archive-delete-days">Archive Auto-Delete (days)</Label>
              <Input
                id="archive-delete-days"
                type="number"
                min="1"
                max="365"
                value={archiveDeleteDays}
                onChange={(e) => setArchiveDeleteDays(parseInt(e.target.value) || 1)}
                disabled={saving}
                className="w-[120px]"
              />
            </div>

            {/* Restore Tags Button with Confirmation */}
            {!showRestoreTagsConfirm ? (
              <Button
                variant="outline"
                onClick={() => setShowRestoreTagsConfirm(true)}
                disabled={restoring}
                className="w-full"
              >
                Restore Tags to Default
              </Button>
            ) : (
              <div className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/50">
                <p className="text-sm text-center">Restore tags to default?</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowRestoreTagsConfirm(false)}
                    className="flex-1"
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleRestoreTags}
                    disabled={restoring}
                    className="flex-1"
                    size="sm"
                  >
                    Yes
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Appearance Tab */}
          <TabsContent value="appearance" className="flex-1 overflow-y-auto space-y-4 pr-2 bg-background">
            <div className="flex items-center justify-between">
              <Label htmlFor="drag-handle">Reordering Controls</Label>
              <Select value={dragHandlePosition} onValueChange={setDragHandlePosition} disabled={saving}>
                <SelectTrigger id="drag-handle" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="horizontal-groups">Storage Group Arrangement</Label>
              <Select 
                value={horizontalGroups ? "horizontal" : "vertical"} 
                onValueChange={(value) => setHorizontalGroups(value === "horizontal")} 
                disabled={saving}
              >
                <SelectTrigger id="horizontal-groups" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vertical">Vertical</SelectItem>
                  <SelectItem value="horizontal">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="storage-active-visibility">Storage Active Task Visibility</Label>
              <Select 
                value={storageActiveVisibility} 
                onValueChange={setStorageActiveVisibility} 
                disabled={saving}
              >
                <SelectTrigger id="storage-active-visibility" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible">Visible</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between" data-testid="task-card-tags-setting-row">
              <Label htmlFor="task-card-tags-display">Task Card Tags</Label>
              <Select 
                value={taskCardTagsDisplay} 
                onValueChange={setTaskCardTagsDisplay} 
                disabled={saving}
              >
                <SelectTrigger id="task-card-tags-display" className="w-[140px]" data-testid="task-card-tags-display-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="icon_text" data-testid="task-card-tags-icon-text-option">Icon + Text</SelectItem>
                  <SelectItem value="icon_only" data-testid="task-card-tags-icon-only-option">Icon Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="tooltip-visibility">Tooltip Visibility</Label>
              <Select 
                value={showTooltips ? "visible" : "hidden"} 
                onValueChange={(value) => {
                  const newValue = value === "visible";
                  setShowTooltips(newValue);
                  localStorage.setItem('showTooltips', newValue.toString());
                  toast.success(newValue ? 'Tooltips visible' : 'Tooltips hidden');
                }} 
                disabled={saving}
              >
                <SelectTrigger id="tooltip-visibility" className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible">Visible</SelectItem>
                  <SelectItem value="hidden">Hidden</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="primary-color">Primary</Label>
                <Input
                  id="primary-color"
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  disabled={saving}
                  className="h-10 w-16"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="secondary-color">Secondary</Label>
                <Input
                  id="secondary-color"
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  disabled={saving}
                  className="h-10 w-16"
                />
              </div>
            </div>

            {/* Reset Colors Button with Confirmation */}
            {!showResetColorsConfirm ? (
              <Button
                variant="outline"
                onClick={() => setShowResetColorsConfirm(true)}
                disabled={saving}
                className="w-full"
              >
                Reset Colors to Default
              </Button>
            ) : (
              <div className="flex flex-col gap-2 p-3 border rounded-lg bg-muted/50">
                <p className="text-sm text-center">Reset colors to default?</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowResetColorsConfirm(false)}
                    className="flex-1"
                    size="sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleResetColors}
                    className="flex-1"
                    size="sm"
                  >
                    Yes
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Cancel and Save buttons - always visible at bottom */}
        <div className="pt-4 border-t mt-4 flex gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1"
            style={{ backgroundColor: 'var(--color-primary)', color: 'white' }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
